import { transition } from '@helpers/animate';
import { imageCoverContainer } from '@helpers/aspectRatio';
import brand, { ThemeOption } from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { baseGrid, siteWide } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { contentStyles, h1, h2 } from '@helpers/typography';
import MotionTrack from '@stories/Components/Carousels/Carousel/MotionTrack/MotionTrack';
import SectionWrapper from '@stories/Components/Global/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Global/SectionWrapper/SectionWrapper.styles';
import { rgba } from 'polished';
import styled from 'styled-components';
const Container = styled.section `
  overflow: hidden;
  padding-bottom: 40px;

  @media ${from(Device.Tablet)} {
    padding-bottom: 80px;
  }
`;
const Track = styled(MotionTrack) ``;
const Item = styled.li `
  display: block;
  flex: 0 0 calc(100% / 1);
  padding-right: var(--itemGutter);
`;
const PaginationWrapper = styled.div `
  ${siteWide};
  display: grid;
  margin-top: ${fluid(20, 24)};
  grid-template-columns: repeat(12, 1fr);
  gap: 0.5rem;
  align-items: center;

  @media ${from(Device.Tablet)} {
    margin-top: ${fluid(80, 80)};
  }

  @media ${from(Device.DesktopSmall)} {
    margin-top: ${fluid(20, 24)};
  }
`;
const Pagination = styled.div `
  grid-column: span 12;

  @media ${from(Device.DesktopSmall)} {
    grid-column: 2 / span 4;
  }
`;
const Slide = styled(SectionWrapper) `
  position: relative;

  ${SectionWrapperStyles.SiteWide} {
    @media ${until(Device.Tablet)} {
      padding-left: 0;
      padding-right: 0;
    }
  }
`;
const Inner = styled.div `
  @media ${from(Device.Tablet)} {
    ${baseGrid};
    height: 550px;
    border: none;
  }
`;
const ContentArea = styled.div `
  @media ${from(Device.Tablet)} {
    position: relative;
    z-index: 1;
    grid-column: span 12;
    height: 100%;
    border-left: 3px solid ${brand.white};
  }

  &[data-element-theme=${ThemeOption.Orange}] {
    --themeColor: ${brand.orange};
    --buttonFgColor: ${brand.orange};
  }

  &[data-element-theme=${ThemeOption.Blue}] {
    --themeColor: ${brand.blue};
    --buttonFgColor: ${brand.blue};
  }

  &[data-element-theme=${ThemeOption.Purple}] {
    --themeColor: ${brand.purple};
    --buttonFgColor: ${brand.purple};
  }

  &[data-element-theme=${ThemeOption.Red}] {
    --themeColor: ${brand.red};
    --buttonFgColor: ${brand.red};
  }

  &[data-element-theme=${ThemeOption.Green}] {
    --themeColor: ${brand.green.default};
    --buttonFgColor: ${brand.green.default};
  }

  &[data-element-theme=${ThemeOption.DarkGreen}] {
    --themeColor: ${brand.green.dark};
    --buttonFgColor: ${brand.green.dark};
  }

  &[data-element-theme=${ThemeOption.Teal}] {
    --themeColor: ${brand.teal};
    --buttonFgColor: ${brand.teal};
  }
`;
const ContentAreaInner = styled.a `
  position: relative;
  display: block;
  background-color: ${brand.grey.grey91};
  padding: 30px ${fluid(35, 40)} 80px ${fluid(35, 60)};
  min-height: 280px;
  width: 100%;
  border-top: ${fluid(10, 20)} solid var(--themeColor);
  color: inherit;
  text-decoration: none;

  @media ${from(Device.Tablet)} {
    position: absolute;
    right: 100px;
    bottom: -60px;
    z-index: 1;
    border-top: none;
    border-left: ${fluid(10, 20)} solid var(--themeColor);
    max-width: 624px;
  }
`;
const BannerTitle = styled.h2 `
  ${h1};
  margin-bottom: 20px;
`;
const SubTitle = styled.h3 `
  ${h2};
  margin-bottom: 15px;

  ${ContentAreaInner}[href]:hover & {
    text-decoration: underline;
  }
`;
const Content = styled.div `
  ${contentStyles};
`;
const CtaArea = styled.div `
  position: absolute;
  bottom: ${fluid(25, 30)};
  right: ${fluid(35, 40)};
`;
const LinkBtn = styled.span `
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${fluid(40, 40)};
  height: ${fluid(40, 40)};
  border-radius: 100%;
  background-color: var(--buttonFgColor);

  &:hover,
  &:focus,
  ${ContentAreaInner}[href]:hover & {
    svg {
      path {
        stroke-width: 5;
      }
    }
  }

  svg {
    width: ${fluid(26, 26)};
    height: ${fluid(26, 26)};
    color: ${brand.white};

    path {
      ${transition('stroke-width')}
    }
  }
`;
const MediaArea = styled.div `
  position: relative;
  height: 200px;
  width: 100%;

  &::before {
    content: '';
    width: ${fluid(2, 3)};
    height: 100%;
    background-color: ${brand.white};
    position: absolute;
    top: 0;
    left: ${fluid(35, 35)};
    z-index: 1;

    @media ${from(Device.Tablet)} {
      content: none;
    }
  }

  &:after {
    content: '';
    width: 100%;
    height: ${fluid(2, 3)};
    background-color: ${brand.white};
    position: absolute;
    bottom: ${fluid(35, 100)};
    left: 0;
  }

  @media ${from(Device.Tablet)} {
    position: absolute;
    width: 100%;
    height: 550px;
    right: 0;
  }
`;
const CitationBtn = styled.button `
  ${transition('background-color')};
  position: absolute;
  right: 0;
  bottom: 0;
  border: none;
  border-radius: 0;
  background-color: transparent;
  height: 40px;
  padding: 0 40px;
  z-index: 2;

  @media ${until(Device.Tablet)} {
    width: 100%;
    height: 35px;
  }

  &[data-citation-open='true'] {
    background-color: ${rgba(brand.white, 0.9)};
  }
`;
const CitationIcon = styled.div `
  ${transition('background-color')};
  ${fonts.agBook.bold};
  color: ${brand.white};
  width: 30px;
  height: 30px;
  background-color: ${rgba(brand.grey.grey41, 0.9)};
  border-radius: 100%;
  font-size: 22px;
  line-height: 30px;
  margin-right: 40px;
  margin-top: -20px;

  @media ${until(Device.Tablet)} {
    position: absolute;
    top: -35px;
    right: 20px;
    margin-right: 0;
    margin-top: 0;
  }

  ${CitationBtn}:hover &,
  ${CitationBtn}:focus {
    background-color: ${brand.orange};
  }

  [data-citation-open='true'] & {
    @media ${from(Device.Tablet)} {
      display: none;
    }
  }
`;
const CitationContent = styled.span `
  ${fonts.agBook.light};
  font-style: italic;
  font-size: 12px;
  line-height: 14px;
  display: none;

  [data-citation-open='true'] & {
    display: block;
  }
`;
const PromoMedia = styled.div `
  ${imageCoverContainer()};
`;
const PromoImage = styled.img `
  ${imageCoverContainer()};
`;
export const HeroBannerCarouselStyles = {
    BannerTitle,
    CitationBtn,
    CitationContent,
    CitationIcon,
    Container,
    Content,
    ContentArea,
    ContentAreaInner,
    CtaArea,
    Inner,
    Item,
    LinkBtn,
    MediaArea,
    Pagination,
    PaginationWrapper,
    PromoImage,
    PromoMedia,
    Slide,
    SubTitle,
    Track,
};
